import { createContext, useContext, useState, useCallback, useMemo, forwardRef, type ReactNode } from "react";

const lazyTrackEvent = (eventName: string, params?: Record<string, any> & { ga4?: any; __options?: any }) => {
  import("@/lib/fbPixel").then(({ trackEvent }) => trackEvent(eventName, params)).catch(() => {});
};

export interface CartItem {
  productId: number | string;
  name: string;
  price: string | number;
  size: string;
  image: string;
  quantity: number;
}

interface CartContextType {
  items: CartItem[];
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  addItem: (item: Omit<CartItem, "quantity">, quantity?: number, openDrawer?: boolean) => void;
  removeItem: (productId: number | string, size: string) => void;
  updateQuantity: (productId: number | string, size: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalAmount: number;
}

const CartContext = createContext<CartContextType | null>(null);

const defaultCart: CartContextType = {
  items: [], isOpen: false, openCart: () => {}, closeCart: () => {},
  addItem: () => {}, removeItem: () => {}, updateQuantity: () => {},
  clearCart: () => {}, totalItems: 0, totalAmount: 0,
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  return ctx ?? defaultCart;
};

const parsePrice = (price: string | number): number =>
  typeof price === "number" ? price : Number(price.replace(/[^0-9.]/g, "")) || 0;

export const CartProvider = forwardRef<HTMLDivElement, { children: ReactNode }>(({ children }, _ref) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  const addItem = useCallback((item: Omit<CartItem, "quantity">, quantity: number = 1, openDrawer: boolean = true) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.productId === item.productId && i.size === item.size);
      if (existing) {
        return prev.map((i) =>
          i.productId === item.productId && i.size === item.size
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      }
      return [...prev, { ...item, quantity }];
    });
    if (openDrawer) setIsOpen(true);
    const unitPrice = parsePrice(item.price);
    const lineValue = unitPrice * quantity;
    lazyTrackEvent("AddToCart", {
      content_name: item.name,
      content_ids: [String(item.productId)],
      content_type: "product",
      value: lineValue,
      currency: "BDT",
      num_items: quantity,
      ga4: {
        currency: "BDT",
        value: lineValue,
        items: [{
          item_id: String(item.productId),
          item_name: item.name,
          item_brand: "Fatema Farista",
          item_variant: item.size || undefined,
          price: unitPrice,
          quantity,
          currency: "BDT",
        }],
      },
    });
  }, []);

  const removeItem = useCallback((productId: number | string, size: string) => {
    setItems((prev) => prev.filter((i) => !(i.productId === productId && i.size === size)));
  }, []);

  const updateQuantity = useCallback((productId: number | string, size: string, quantity: number) => {
    if (quantity < 1) return;
    setItems((prev) =>
      prev.map((i) =>
        i.productId === productId && i.size === size ? { ...i, quantity } : i
      )
    );
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const totalItems = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items]);
  const totalAmount = useMemo(() => items.reduce((sum, i) => sum + parsePrice(i.price) * i.quantity, 0), [items]);

  const value = useMemo<CartContextType>(() => ({
    items, isOpen, openCart, closeCart, addItem, removeItem, updateQuantity, clearCart, totalItems, totalAmount,
  }), [items, isOpen, openCart, closeCart, addItem, removeItem, updateQuantity, clearCart, totalItems, totalAmount]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
});

CartProvider.displayName = "CartProvider";
